import "../../css/PaymentMethods.css";
import { useState, useEffect } from "react";
import { Flex, Row, Col } from 'antd';
import { useQuery } from "@apollo/client";
import { getFromLocalStorage, saveToLocalStorage } from "../../utils/localStorage";
import { GET_MARKETS, GET_PAYMENT_METHOD_SECTION, GET_MARKET_PAYMENT_METHODS } from "../../queries";
import { parseMarkets, parsePaymentMethods } from "../../utils/parseMarkets";
import useIsMobile from '../../utils/isMobile';
import { paymentMethodTypes } from "../../langs/PaymentMethods";
import Desktop from "./Desktop";
import Mobile from "./Mobile";

const usePaymentMethods = (selectedCountry) => {
	const { data, loading, error } = useQuery(GET_MARKET_PAYMENT_METHODS, {
		skip: !selectedCountry,
		variables: { slug: selectedCountry?.slug }
	});
	return { selectedPaymentMethods: data, loading }
};


const Payments = ({ page, section, lang, country = 'argentina' }) => {
	const isMobile = useIsMobile();
	const [marketsData, setMarketsData] = useState(() => {
		const cachedData = getFromLocalStorage('markets');
		return cachedData?.markets || [];
	});
	const marketsQuery = useQuery(GET_MARKETS, {
		skip: marketsData.length > 0,
		onCompleted: (fetchedData) => {
			const markets = parseMarkets(fetchedData?.markets);
			saveToLocalStorage('markets', markets);
			setMarketsData(markets);
		},
	});

	const slug = `${page}-${section}-${lang}`;
	const [titles, setTitles] = useState(() => {
		const cachedData = getFromLocalStorage(slug);
		return cachedData?.sectionsBy?.paymentMethodSection || null;
	});
	const titlesQuery = useQuery(GET_PAYMENT_METHOD_SECTION, {
		variables: { slug },
		skip: !!titles,
		onCompleted: (fetchedData) => {
			const titulo = fetchedData.sectionsBy?.paymentMethodSection?.tituloPM;
			const subtituloMovil = fetchedData.sectionsBy?.paymentMethodSection?.subtituloMovil;
			const dataToLocal = {
				...marketsData,
				[slug]: {
					titulo,
					subtituloMovil
				},
			};
			saveToLocalStorage(page, dataToLocal);
			setTitles({ titulo, subtituloMovil } || null);
		},
	});


	const [selectedCountry, setSelectedCountry] = useState(marketsData.length > 0 ? marketsData[0] : null);
	const [defaultRegion, setDefaultRegion] = useState("Latam");
	useEffect(() => {
		let selected;
		if (country) {
			selected = marketsData.find((data) => data.slug === country)
		} else if (marketsData.length > 0) {
			selected = marketsData.find((data) => data.slug === marketsData[0].slug)
		}
		setSelectedCountry(selected);
		setDefaultRegion(selected?.region);
	}, [marketsData, country]);

	const [selectedKey, setSelectedKey] = useState(country);
	const handleMenuClick = ({ key }) => {
		setSelectedKey(key);
		const country = marketsData.find((country) => country.slug === key);
		setSelectedCountry(country);
	};

	const groupedData = marketsData.reduce((acc, item) => {
		if (!acc[item.region]) acc[item.region] = [];
		acc[item.region].push(item);
		acc[item.region].sort((a, b) => {
			return a.country.localeCompare(b.country);
		});
		return acc;
	}, {});

	const reversedGroupedData = Object.keys(groupedData)
		.reverse()
		.reduce((acc, region) => {
			acc[region] = groupedData[region];
			return acc;
		}, {});

	const { selectedPaymentMethods, loading } = usePaymentMethods(selectedCountry);
	useEffect(() => {
		if (selectedPaymentMethods && selectedCountry)
			setSelectedCountry({
				...selectedCountry,
				cardPaymentMethods: parsePaymentMethods(selectedPaymentMethods?.marketBy.cardPaymentMethods),
				cashPaymentMethods: parsePaymentMethods(selectedPaymentMethods?.marketBy.cashPaymentMethods),
				eWalletsPaymentMethods: parsePaymentMethods(selectedPaymentMethods?.marketBy.eWalletsPaymentMethods),
				mobilePaymentMethods: parsePaymentMethods(selectedPaymentMethods?.marketBy.mobilePaymentMethods),
				onlinePaymentMethods: parsePaymentMethods(selectedPaymentMethods?.marketBy.onlinePaymentMethods),
			})
	}, [selectedPaymentMethods]);


	const paymentTypes = [
		{ title: paymentMethodTypes[lang]["Cash"], methods: selectedCountry?.cashPaymentMethods || [] },
		{ title: paymentMethodTypes[lang]["Card"], methods: selectedCountry?.cardPaymentMethods || [] },
		{ title: paymentMethodTypes[lang]["E-wallets"], methods: selectedCountry?.eWalletsPaymentMethods || [] },
		{ title: paymentMethodTypes[lang]["Mobile"], methods: selectedCountry?.mobilePaymentMethods || [] },
		{ title: paymentMethodTypes[lang]["Online"], methods: selectedCountry?.onlinePaymentMethods || [] },
	];

	useEffect(() => {
		if (marketsData.length === 0 || !selectedCountry) return;
		const updatedMarketsData = marketsData.map((country) =>
			country.slug === selectedCountry.slug
				? { ...country, ...selectedCountry }
				: country
		);
		saveToLocalStorage('markets', updatedMarketsData);
	}, [selectedCountry])
	return (
		<>
			{
				marketsData.length > 0 && (
					<Flex vertical className="PaymentMethods">
						<Row>
							<Col span={24}>
								<Flex className="PaymentMethods-title">
									<Flex vertical>
										<h2
											className="h4-headline-500"
											dangerouslySetInnerHTML={{ __html: titles?.titulo }}
										/>
									</Flex>
								</Flex>
							</Col>
						</Row>
						{isMobile ? (
							<Mobile
								selectedCountry={selectedCountry}
								setSelectedCountry={setSelectedCountry}
								marketsData={groupedData}
								paymentTypes={paymentTypes}
								subtitle={titles?.subtituloMovil}
								loading={loading}
								lang={lang}
							/>
						) : (
							<Desktop
								paymentTypes={paymentTypes}
								groupedData={reversedGroupedData}
								handleMenuClick={handleMenuClick}
								selectedKey={selectedKey}
								selectedCountry={selectedCountry}
								loading={loading}
								lang={lang}
								defaultRegion={defaultRegion}
							/>
						)}
					</Flex>
				)
			}
		</>
	);
};

export default Payments;
